import { apiClient } from '@/common/lib/api-client';

/**
 * Create a new CRM contact
 * POST /v1/leads/companies/{company_id}/contacts/create/
 */
export async function createCRMContact({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/create/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the list of CRM contacts
 * GET /v1/leads/companies/{company_id}/contacts/
 */
export async function getCRMContacts(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/`,
    { params },
  );
  return data;
}

/**
 * Retrieve a single CRM contact
 * GET /v1/leads/companies/{company_id}/contacts/{contact_id}/
 */
export async function getCRMContactDetail({ companyId, contactId }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/`,
  );
  return data;
}

/**
 * Update an existing CRM contact
 * PATCH /v1/leads/companies/{company_id}/contacts/{contactId}/update/
 */
export async function updateCRMContact({ companyId, contactId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/update/`,
    payload,
  );
  return data;
}

/**
 * Delete a CRM contact
 * DELETE /v1/leads/companies/{company_id}/contacts/{contactId}/
 */
export async function deleteCRMContact({ companyId, contactId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/`,
  );
  return data;
}

/**
 * Soft delete multiple CRM contacts
 * POST /v1/leads/companies/{company_id}/contacts/bulk-delete/
 */
export async function bulkDeleteCRMContacts({
  companyId,
  contactIds,
  selection,
}) {
  const body = selection ? { selection } : { contact_ids: contactIds };
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/bulk-delete/`,
    body,
  );
  return data;
}

/**
 * Create a source for a CRM contact
 * POST /v1/leads/companies/{company_id}/contacts/{contact_id}/sources/create/
 */
export async function createCRMContactSource({
  companyId,
  contactId,
  payload,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/sources/create/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the list of sources for a CRM contact
 * GET /v1/leads/companies/{company_id}/contacts/{contact_id}/sources/
 */
export async function getCRMContactSources({ companyId, contactId }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/sources/`,
  );
  return data;
}

function entityTagsUrl(companyId, entityPath, entityId) {
  return `/v1/leads/companies/${companyId}/${entityPath}/${entityId}/tags/`;
}

/**
 * Full-replace tags for a contact or employee.
 * POST /v1/leads/companies/{company_id}/{contacts|employees}/{id}/tags/
 * Body: { tag_ids: number[] } — empty array clears all tags.
 */
async function assignEntityTags({ companyId, entityPath, entityId, payload }) {
  const { data } = await apiClient.post(
    entityTagsUrl(companyId, entityPath, entityId),
    payload,
  );
  return data;
}

/**
 * Manage (set/update) tags for a CRM contact
 * POST /v1/leads/companies/{company_id}/contacts/{contact_id}/tags/
 */
export async function manageCRMContactTags({ companyId, contactId, payload }) {
  return assignEntityTags({
    companyId,
    entityPath: 'contacts',
    entityId: contactId,
    payload,
  });
}

/**
 * Full-replace tags for an employee (same company tag catalog as contacts).
 * POST /v1/leads/companies/{company_id}/employees/{employee_id}/tags/
 * @returns {Promise<{ id: number, tags?: { id: number, name: string, color: string }[], [key: string]: unknown }>}
 */
export async function manageEmployeeTags({ companyId, employeeId, payload }) {
  return assignEntityTags({
    companyId,
    entityPath: 'employees',
    entityId: employeeId,
    payload,
  });
}

/**
 * Manage (set/update) consent status for a CRM contact
 * PATCH /v1/leads/companies/{company_id}/contacts/{contact_id}/consent/
 */
export async function manageCRMContactConsent({
  companyId,
  contactId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/consent/`,
    payload,
  );
  return data;
}

/**
 * Assigns the specified contact to a company member (user).
 * POST /v1/leads/companies/{company_id}/contacts/{contact_id}/assign/
 */
export async function assignCRMContact({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/assign/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the chronological activity feed/timeline for a contact
 * GET /v1/leads/companies/{company_id}/contacts/{contact_id}/timeline/
 */
export async function getCRMContactTimeline({
  companyId,
  contactId,
  page = 1,
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/timeline/?page=${page}`,
  );
  return data;
}

/**
 * Add a note to a CRM contact (also creates an activity)
 * POST /v1/leads/companies/{company_id}/contacts/{contact_id}/notes/
 */
export async function addCRMContactNote({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the consent history for a CRM contact
 * GET /v1/leads/companies/{company_id}/contacts/{contact_id}/consent-history/
 */
export async function getCRMContactConsentHistory({ companyId, contactId }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/consent-history/`,
  );
  return data;
}

/**
 * Retrieve the list of CRM tags
 * GET /v1/leads/companies/{company_id}/tags/
 */
export async function getCRMTags(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/tags/`,
    {
      params,
    },
  );
  return data;
}

/**
 * Retrieve details for a specific CRM tag
 * GET /v1/leads/companies/{company_id}/tags/{tag_id}/
 */
export async function getCRMTagDetail({ companyId, tagId }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/tags/${tagId}/`,
  );
  return data;
}

/**
 * Create a new CRM tag
 * POST /v1/leads/companies/{company_id}/tags/create/
 */
export async function createCRMTag({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/tags/create/`,
    payload,
  );
  return data;
}

/**
 * Update a CRM tag
 * PATCH /v1/leads/companies/{company_id}/tags/{tag_id}/update/
 */
export async function updateCRMTag({ companyId, tagId, payload }) {
  const { data } = await apiClient.put(
    `/v1/leads/companies/${companyId}/tags/${tagId}/update/`,
    payload,
  );
  return data;
}

/**
 * Delete a CRM tag
 * DELETE /v1/leads/companies/{company_id}/tags/{tag_id}/delete/
 */
export async function deleteCRMTag({ companyId, tagId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/tags/${tagId}/delete/`,
  );
  return data;
}

/**
 * Retrieve the list of CRM pipeline stages
 * GET /v1/leads/companies/{company_id}/pipeline/stages/
 */
export async function getCRMPipelineStages(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/pipeline/stages/`,
  );
  return data;
}

/**
 * Retrieve the list of CRM custom fields
 * GET /v1/leads/companies/{company_id}/custom-fields/
 */
export async function getCRMCustomFields(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/custom-fields/`,
    {
      params,
    },
  );
  return data;
}

/**
 * Export CRM contacts to CSV
 * POST /v1/leads/companies/{company_id}/contacts/export/
 */
export async function exportCRMContacts({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/export/`,
    payload,
  );
  return data;
}

/**
 * Import CRM contacts via CSV
 * POST /v1/leads/companies/{company_id}/contacts/import/
 */
export async function importCRMContacts({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/import/`,
    payload,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return data;
}

/**
 * Retrieve details for a specific CRM custom field
 * GET /v1/leads/companies/{company_id}/custom-fields/{field_id}/
 */
export async function getCRMCustomFieldDetail({ companyId, fieldId }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/custom-fields/${fieldId}/`,
  );
  return data;
}

/**
 * Create a new CRM custom field
 * POST /v1/leads/companies/{company_id}/custom-fields/create/
 */
export async function createCRMCustomField({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/custom-fields/create/`,
    payload,
  );
  return data;
}

/**
 * Update a CRM custom field
 * PUT /v1/leads/companies/{company_id}/custom-fields/{field_id}/update/
 */
export async function updateCRMCustomField({ companyId, fieldId, payload }) {
  const { data } = await apiClient.put(
    `/v1/leads/companies/${companyId}/custom-fields/${fieldId}/update/`,
    payload,
  );
  return data;
}

/**
 * Delete a CRM custom field
 * DELETE /v1/leads/companies/{company_id}/custom-fields/{field_id}/delete/
 */
export async function deleteCRMCustomField({ companyId, fieldId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/custom-fields/${fieldId}/delete/`,
  );
  return data;
}

/**
 * Retrieve the list of notes for a CRM contact
 * GET /v1/leads/companies/{company_id}/contacts/{contact_id}/notes/
 */
export async function getCRMContactNotes({
  companyId,
  contactId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/`,
    { params },
  );
  return data;
}

/**
 * Create a new note for a CRM contact
 * POST /v1/leads/companies/{company_id}/contacts/{contact_id}/notes/create/
 */
export async function createCRMContactNote({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/create/`,
    payload,
  );
  return data;
}

/**
 * Update a note for a CRM contact
 * PATCH /v1/leads/companies/{company_id}/contacts/{contact_id}/notes/{note_id}/update/
 */
export async function updateCRMContactNote({
  companyId,
  contactId,
  noteId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/${noteId}/update/`,
    payload,
  );
  return data;
}

/**
 * Delete a note for a CRM contact
 * DELETE /v1/leads/companies/{company_id}/contacts/{contact_id}/notes/{note_id}/delete/
 */
export async function deleteCRMContactNote({ companyId, contactId, noteId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/notes/${noteId}/delete/`,
  );
  return data;
}

/**
 * Create a CRM pipeline stage
 * POST /v1/leads/companies/{company_id}/pipeline/stages/
 */
export async function createCRMPipelineStage({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/pipeline/stages/`,
    payload,
  );
  return data;
}

/**
 * Update a CRM pipeline stage
 * PATCH /v1/leads/companies/{company_id}/pipeline/stages/{stage_id}/
 */
export async function updateCRMPipelineStage({ companyId, stageId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/pipeline/stages/${stageId}/`,
    payload,
  );
  return data;
}

/**
 * Reorder CRM pipeline stages
 * POST /v1/leads/companies/{company_id}/pipeline/stages/reorder/
 */
export async function reorderCRMPipelineStages({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/pipeline/stages/reorder/`,
    payload,
  );
  return data;
}

/**
 * Retrieve the CRM pipeline board data
 * GET /v1/leads/companies/{company_id}/pipeline/board/
 */
export async function getCRMPipelineBoard(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/pipeline/board/`,
    { params },
  );
  return data;
}

/**
 * Move a CRM contact to a different pipeline stage
 * PATCH /v1/leads/companies/{company_id}/contacts/{contact_id}/move-stage/
 */
export async function moveCRMContactStage({ companyId, contactId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/move-stage/`,
    payload,
  );
  return data;
}

export async function getCRMContactTasks({
  companyId,
  contactId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/`,
    { params },
  );
  return data;
}

export async function getCRMContactTaskDetail({
  companyId,
  contactId,
  taskId,
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/${taskId}/`,
  );
  return data;
}

export async function createCRMContactTask({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/create/`,
    payload,
  );
  return data;
}

export async function updateCRMContactTask({
  companyId,
  contactId,
  taskId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/${taskId}/update/`,
    payload,
  );
  return data;
}

export async function deleteCRMContactTask({ companyId, contactId, taskId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/${taskId}/delete/`,
  );
  return data;
}

export async function completeCRMContactTask({ companyId, contactId, taskId }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/tasks/${taskId}/complete/`,
  );
  return data;
}

// ==========================================
// CRM Segments API
// ==========================================

/**
 * Retrieve the list of CRM Segments
 * GET /v1/leads/companies/{company_id}/segments/
 */
export async function getCRMSegments(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/segments/`,
    { params },
  );
  return data;
}

/**
 * Create a new CRM Segment
 * POST /v1/leads/companies/{company_id}/segments/
 */
export async function createCRMSegment({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/segments/`,
    payload,
  );
  return data;
}

/**
 * Retrieve a single CRM Segment
 * GET /v1/leads/companies/{company_id}/segments/{segment_id}/
 */
export async function getCRMSegmentDetail({ companyId, segmentId, params }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/segments/${segmentId}/`,
    { params },
  );
  return data;
}

/**
 * Update a CRM Segment
 * PATCH /v1/leads/companies/{company_id}/segments/{segment_id}/
 */
export async function updateCRMSegment({ companyId, segmentId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/segments/${segmentId}/`,
    payload,
  );
  return data;
}

/**
 * Delete a CRM Segment
 * DELETE /v1/leads/companies/{company_id}/segments/{segment_id}/
 */
export async function deleteCRMSegment({ companyId, segmentId }) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/segments/${segmentId}/`,
  );
  return data;
}

/**
 * Evaluate an existing Segment
 * POST /v1/leads/companies/{company_id}/segments/{segment_id}/evaluate/
 */
export async function evaluateCRMSegment({ companyId, segmentId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/segments/${segmentId}/evaluate/`,
    payload,
  );
  return data;
}

/**
 * Evaluate Segment Rules (Live Preview)
 * POST /v1/leads/companies/{company_id}/segments/evaluate/
 */
export async function evaluateCRMSegmentRules({ companyId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/segments/evaluate/`,
    payload,
  );
  return data;
}

/**
 * Push a CRM Segment to integrations
 * POST /v1/leads/companies/{company_id}/segments/{segment_id}/push/
 */
export async function pushCRMSegment({ companyId, segmentId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/segments/${segmentId}/push/`,
    payload,
  );
  return data;
}

/**
 * Retrieve filter fields for CRM Segments
 * GET /v1/leads/companies/{company_id}/segments/filter-fields/
 */
export async function getCRMSegmentFilterFields(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/segments/filter-fields/`,
  );
  return data;
}

// ==========================================
// CRM Attachments API
// ==========================================

export async function getCRMAttachments({ companyId, contactId, params = {} }) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/attachments/`,
    { params },
  );
  return data;
}

export async function getCRMAttachmentDetail({
  companyId,
  contactId,
  attachmentId,
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/attachments/${attachmentId}/`,
  );
  return data;
}

export async function createCRMAttachment({ companyId, contactId, payload }) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/attachments/create/`,
    payload,
    {
      transformRequest: [
        (data, headers) => {
          delete headers['Content-Type'];
          return data;
        },
      ],
    },
  );
  return data;
}

export async function updateCRMAttachment({
  companyId,
  contactId,
  attachmentId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/attachments/${attachmentId}/update/`,
    payload,
  );
  return data;
}

export async function deleteCRMAttachment({
  companyId,
  contactId,
  attachmentId,
}) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/attachments/${attachmentId}/delete/`,
  );
  return data;
}

export async function getCRMContactFollowUps({
  companyId,
  contactId,
  params = {},
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/`,
    { params },
  );
  return data;
}

export async function getCRMContactFollowUpDetail({
  companyId,
  contactId,
  followUpId,
}) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/`,
  );
  return data;
}

export async function createCRMContactFollowUp({
  companyId,
  contactId,
  payload,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/`,
    payload,
  );
  return data;
}

export async function pauseCRMContactFollowUp({
  companyId,
  contactId,
  followUpId,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/pause/`,
  );
  return data;
}

export async function resumeCRMContactFollowUp({
  companyId,
  contactId,
  followUpId,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/resume/`,
  );
  return data;
}

export async function rescheduleCRMContactFollowUp({
  companyId,
  contactId,
  followUpId,
  payload,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/reschedule/`,
    payload,
  );
  return data;
}

export async function stopCRMContactFollowUp({
  companyId,
  contactId,
  followUpId,
}) {
  const { data } = await apiClient.post(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/stop/`,
  );
  return data;
}

export async function deleteCRMContactFollowUp({
  companyId,
  contactId,
  followUpId,
}) {
  const { data } = await apiClient.delete(
    `/v1/leads/companies/${companyId}/contacts/${contactId}/follow-ups/${followUpId}/delete/`,
  );
  return data;
}

export async function getWhatsappTemplates(companyId, params = {}) {
  const { data } = await apiClient.get(
    `/v1/whatsapp/companies/${companyId}/templates/`,
    { params },
  );
  if (!data || Array.isArray(data) || Array.isArray(data.results)) return data;
  if (Array.isArray(data.templates))
    return { ...data, results: data.templates };
  return data;
}

const stageAutomationsPath = (companyId) =>
  `/v1/leads/companies/${companyId}/pipeline/stage-automations`;

export async function listStageAutomations(companyId, params = {}) {
  const { data } = await apiClient.get(`${stageAutomationsPath(companyId)}/`, {
    params,
  });
  return data;
}

export async function createStageAutomation({ companyId, payload }) {
  const { data } = await apiClient.post(
    `${stageAutomationsPath(companyId)}/`,
    payload,
  );
  return data;
}

export async function getStageAutomation({ companyId, automationId }) {
  const { data } = await apiClient.get(
    `${stageAutomationsPath(companyId)}/${automationId}/`,
  );
  return data;
}

export async function updateStageAutomation({
  companyId,
  automationId,
  payload,
}) {
  const { data } = await apiClient.patch(
    `${stageAutomationsPath(companyId)}/${automationId}/`,
    payload,
  );
  return data;
}

export async function deleteStageAutomation({ companyId, automationId }) {
  const { data } = await apiClient.delete(
    `${stageAutomationsPath(companyId)}/${automationId}/`,
  );
  return data;
}

export async function toggleStageAutomation({
  companyId,
  automationId,
  isActive,
}) {
  const { data } = await apiClient.patch(
    `${stageAutomationsPath(companyId)}/${automationId}/toggle/`,
    isActive === undefined ? {} : { is_active: isActive },
  );
  return data;
}

export async function previewApplyExisting({ companyId, automationId }) {
  const { data } = await apiClient.get(
    `${stageAutomationsPath(companyId)}/${automationId}/apply-existing/`,
  );
  return data;
}

export async function confirmApplyExisting({ companyId, automationId }) {
  const response = await apiClient.post(
    `${stageAutomationsPath(companyId)}/${automationId}/apply-existing/`,
    { confirm: true },
  );
  return { status: response.status, data: response.data };
}

export async function listStageAutomationLogs({
  companyId,
  automationId,
  params = {},
}) {
  const path = automationId
    ? `${stageAutomationsPath(companyId)}/${automationId}/logs/`
    : `${stageAutomationsPath(companyId)}/logs/`;
  const { data } = await apiClient.get(path, { params });
  return data;
}

export async function cancelStageAutomationExecution({
  companyId,
  executionId,
}) {
  const { data } = await apiClient.post(
    `${stageAutomationsPath(companyId)}/executions/${executionId}/cancel/`,
  );
  return data;
}

// ==========================================
// CRM Smart Assignment API
// ==========================================

export async function getCRMAssignmentSettings(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/assignment-settings/`,
  );
  return data;
}

export async function updateCRMAssignmentSettings({ companyId, payload }) {
  const { data } = await apiClient.patch(
    `/v1/leads/companies/${companyId}/assignment-settings/`,
    payload,
  );
  return data;
}

export async function getCRMAssignmentQueue(companyId) {
  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/assignment-queue/`,
  );
  return data;
}

/**
 * Retrieve CRM dashboard tasks and follow-ups.
 * GET /v1/leads/companies/{companyId}/crm-dashboard/tasks-and-followups/
 */
export async function getCRMTasksAndFollowups(companyId, params = {}) {
  const cleanParams = {};
  if (params.type) cleanParams.type = params.type;
  if (params.status) cleanParams.status = params.status;
  if (params.contact_id) cleanParams.contact_id = params.contact_id;
  if (
    params.search &&
    typeof params.search === 'string' &&
    params.search.trim()
  ) {
    cleanParams.search = params.search.trim();
  }
  if (params.page != null) cleanParams.page = params.page;
  if (params.page_size != null) cleanParams.page_size = params.page_size;

  const { data } = await apiClient.get(
    `/v1/leads/companies/${companyId}/crm-dashboard/tasks-and-followups/`,
    { params: cleanParams },
  );
  return data;
}

// Re-export Google Workspace & Sheets API methods for CRM
export {
  getGoogleWorkspaceStatus,
  getGoogleWorkspaceAuthUrl,
  disconnectGoogleWorkspace,
  getGoogleWorkspacePickerSession,
  getGoogleWorkspaceSheets,
  importGoogleWorkspaceSheet,
  exportGoogleWorkspaceSheet,
} from '../../api';
